import { useState } from "react";
import clsx from "clsx";
import { useGameStore } from "../store/useGameStore";
import { adminApi } from "../api/admin";

export function AdminCharacterScreen() {
  const { setScreen, hero, balance, refreshHero, refreshBalance } = useGameStore();
  const [goldAmount, setGoldAmount] = useState("1000000");
  const [diamondAmount, setDiamondAmount] = useState("100");
  const [starDustAmount, setStarDustAmount] = useState("100");
  const [xpGap, setXpGap] = useState("10");
  const [loading, setLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runBoost = async (key: string, fn: () => Promise<{ message: string; hero?: typeof hero; balance?: typeof balance }>) => {
    setLoading(key);
    setMessage(null);
    setError(null);
    try {
      const res = await fn();
      if (res.hero) {
        useGameStore.setState({ hero: res.hero });
      } else {
        await refreshHero();
      }
      if (res.balance) {
        useGameStore.setState({ balance: res.balance });
      } else {
        await refreshBalance();
      }
      setMessage(res.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setLoading(null);
    }
  };

  const parseAmount = (value: string) => {
    const amount = Number.parseInt(value, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Enter a positive number");
    }
    if (amount > 1_000_000_000_000) {
      throw new Error("Maximum grant is 1,000,000,000,000");
    }
    return amount;
  };

  const parseGap = (value: string) => {
    const gap = Number.parseInt(value, 10);
    if (!Number.isFinite(gap) || gap <= 0) {
      throw new Error("Enter a positive XP gap");
    }
    return gap;
  };

  if (!hero) {
    return (
      <div className="p-4 text-gray-400 text-center py-12">
        Create a hero first to use character tools.
      </div>
    );
  }

  const isMaxLevel = hero.xp_to_next === 0;

  return (
    <div className="p-4 space-y-4 pb-8">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScreen("admin")}
          className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
        >
          ← Back
        </button>
        <h1 className="text-xl font-bold font-game text-gold-400">🦸 Character (Self)</h1>
      </div>

      <p className="text-gray-400 text-sm">
        Boost your own hero for testing. Changes apply only to the logged-in admin account.
      </p>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h2 className="font-bold text-white">Current state</h2>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <StatCard label="Level" value={String(hero.level)} />
          <StatCard label="Skill points" value={String(hero.skill_points)} />
          <StatCard label="XP" value={hero.xp.toLocaleString()} />
          <StatCard
            label="To next level"
            value={isMaxLevel ? "MAX" : `${hero.xp_to_next.toLocaleString()} XP`}
          />
          <StatCard label="Gold" value={(balance?.gold ?? 0).toLocaleString()} />
          <StatCard label="Diamonds" value={(balance?.diamonds ?? 0).toLocaleString()} />
          <StatCard label="Star dust" value={(balance?.star_dust ?? 0).toLocaleString()} />
          <StatCard label="Arcane dust" value={(balance?.arcane_dust ?? 0).toLocaleString()} />
        </div>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">Level prep</h2>
          <p className="text-xs text-gray-400 mt-1">
            Set XP so only a small amount is missing before the next level. Win the next battle to level up legally.
          </p>
        </div>

        <label className="block text-xs text-gray-400">
          XP gap before level-up
          <input
            type="number"
            min={1}
            value={xpGap}
            onChange={(e) => setXpGap(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
          />
        </label>

        <button
          onClick={() =>
            runBoost("near-level", () => adminApi.nearLevelUp(parseGap(xpGap)))
          }
          disabled={loading !== null || isMaxLevel}
          className={clsx(
            "w-full py-2.5 rounded-lg text-sm font-semibold transition-all",
            isMaxLevel
              ? "bg-gray-800 text-gray-500 cursor-not-allowed"
              : "bg-kingdom-mid hover:bg-kingdom-accent/40 text-white disabled:opacity-50",
          )}
        >
          {loading === "near-level"
            ? "Working…"
            : isMaxLevel
              ? "Already at max level"
              : `Set near level-up (${xpGap || "10"} XP left)`}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">Gold</h2>
          <p className="text-xs text-gray-400 mt-1">Add gold to your balance.</p>
        </div>

        <label className="block text-xs text-gray-400">
          Amount
          <input
            type="number"
            min={1}
            max={1_000_000_000_000}
            value={goldAmount}
            onChange={(e) => setGoldAmount(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
          />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {(
            [
              { label: "1M", value: 1_000_000 },
              { label: "10M", value: 10_000_000 },
              { label: "50M", value: 50_000_000 },
            ] as const
          ).map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setGoldAmount(String(preset.value))}
              className="py-2 rounded-lg bg-kingdom-dark border border-kingdom-mid text-xs text-gray-300 hover:border-kingdom-glow"
            >
              {preset.label}
            </button>
          ))}
        </div>

        <button
          onClick={() =>
            runBoost("gold", () => adminApi.grantGold(parseAmount(goldAmount)))
          }
          disabled={loading !== null}
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "gold" ? "Working…" : `Grant ${Number(goldAmount || 0).toLocaleString()} gold`}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">Diamonds</h2>
          <p className="text-xs text-gray-400 mt-1">Add diamonds to your balance.</p>
        </div>

        <label className="block text-xs text-gray-400">
          Amount
          <input
            type="number"
            min={1}
            value={diamondAmount}
            onChange={(e) => setDiamondAmount(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
          />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {[10, 100, 1000].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setDiamondAmount(String(preset))}
              className="py-2 rounded-lg bg-kingdom-dark border border-kingdom-mid text-xs text-gray-300 hover:border-kingdom-glow"
            >
              {preset.toLocaleString()}
            </button>
          ))}
        </div>

        <button
          onClick={() =>
            runBoost("diamonds", () => adminApi.grantDiamonds(parseAmount(diamondAmount)))
          }
          disabled={loading !== null}
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "diamonds"
            ? "Working…"
            : `Grant ${Number(diamondAmount || 0).toLocaleString()} diamonds`}
        </button>
      </section>

      <section className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div>
          <h2 className="font-bold text-white">Star dust</h2>
          <p className="text-xs text-gray-400 mt-1">Add star dust for astral upgrades.</p>
        </div>

        <label className="block text-xs text-gray-400">
          Amount
          <input
            type="number"
            min={1}
            value={starDustAmount}
            onChange={(e) => setStarDustAmount(e.target.value)}
            className="mt-1 w-full rounded-lg bg-kingdom-dark border border-kingdom-mid px-3 py-2 text-white"
          />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {[50, 500, 5000].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setStarDustAmount(String(preset))}
              className="py-2 rounded-lg bg-kingdom-dark border border-kingdom-mid text-xs text-gray-300 hover:border-kingdom-glow"
            >
              {preset.toLocaleString()}
            </button>
          ))}
        </div>

        <button
          onClick={() =>
            runBoost("star-dust", () => adminApi.grantStarDust(parseAmount(starDustAmount)))
          }
          disabled={loading !== null}
          className="w-full py-2.5 rounded-lg bg-kingdom-mid hover:bg-kingdom-accent/40 text-white text-sm font-semibold disabled:opacity-50"
        >
          {loading === "star-dust"
            ? "Working…"
            : `Grant ${Number(starDustAmount || 0).toLocaleString()} star dust`}
        </button>
      </section>

      {message && (
        <div className="text-xs text-emerald-300 bg-emerald-950/40 border border-emerald-800 rounded-lg p-3">
          {message}
        </div>
      )}
      {error && (
        <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded-lg p-3">
          {error}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-kingdom-dark rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}
